import { type CustomCellEditorProps } from "ag-grid-react";
import { isValid, parseISO } from "date-fns";
import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTDatePickerDate } from "commonComponents/btWrappers/BTDatePicker/BTDatePickerDate";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";

import {
    type CustomFieldAssociatedType,
    type DateCustomFieldDefinition,
    type DateCustomFieldValue,
} from "handlers";

import "./DateCellEditor.less";

function parseDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    if (value instanceof Date) return isValid(value) ? value : null;
    if (typeof value !== "string") return null;
    const date = parseISO(value);
    if (isValid(date)) return date;
    const fallback = new Date(value);
    return isValid(fallback) ? fallback : null;
}

const DateCellEditor = (props: CustomCellEditorProps<unknown, string>) => {
    const { value, onValueChange, stopEditing } = props;
    const dateValue = parseDate(value ?? null);

    return (
        <div className="CustomFieldDateCellEditor" data-testid="customFieldDateEditor">
            <BTDatePickerDate
                className="CustomFieldDatePicker"
                id="customFieldDateEditor"
                data-testid="customFieldDateEditorPicker"
                value={dateValue}
                onChange={(_, date) => {
                    onValueChange(date ? date.toISOString() : "");
                    stopEditing();
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                allowClear
            />
        </div>
    );
};

export const DateCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    DateCustomFieldValue,
    DateCustomFieldDefinition
> = {
    getValue(field): string | null {
        return field.value || "";
    },

    setValue(field, value): DateCustomFieldValue {
        return { ...field, value: value ?? "" };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): DateCustomFieldValue {
        return {
            dataType: "Date",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            value: value ?? "",
        };
    },

    compare(valueA: string | null, valueB: string | null): number {
        const a = parseDate(valueA);
        const b = parseDate(valueB);

        if (!a && !b) return 0;
        if (!a) return -1;
        if (!b) return 1;
        return a.getTime() - b.getTime();
    },

    renderDisplay(value: string | null): ReactNode {
        const date = parseDate(value);
        if (!date) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        return <DateDisplayDate value={date} data-testid="dateCustomFieldDisplay" />;
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string | null) => void,
        onBlur: () => void,
        autoFocus?: boolean,
    ): ReactNode {
        const dateValue = parseDate(value);

        return (
            <BTDatePickerDate
                id={`dateCustomFieldEditor-${customFieldId}`}
                data-testid="dateCustomFieldEditor"
                value={dateValue}
                onChange={(_field, date) => {
                    onChange(date ? date.toISOString() : null);
                }}
                onBlur={() => onBlur()}
                formMode="click-to-edit"
                allowClear
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={autoFocus}
            />
        );
    },

    getAgGridCellEditor() {
        return DateCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
